import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Landmark, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDateTime } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { useIsDemo } from "@/demo/DemoContext";

// Past this many banks a scrolling list stops being scannable, so a search box appears above it.
const BANK_SEARCH_THRESHOLD = 8;

type PaymentInstitution = components["schemas"]["PaymentInstitutionResponse"];
type BankConnection = components["schemas"]["BankConnectionResponse"];
type YapilyDisclosure = components["schemas"]["YapilyDisclosure"];
type BillPaymentResponse = components["schemas"]["BillPaymentResponse"];

export function billPaymentStatusLabel(status: BillPaymentResponse["status"] | undefined): string {
  switch (status) {
    case "Initiating":
      return "Starting payment";
    case "AwaitingAuthorisation":
      return "Waiting for bank authorisation";
    case "Authorised":
      return "Authorised - executing";
    case "Pending":
      return "Processing at the bank";
    case "Completed":
      return "Completed";
    case "Failed":
      return "Failed";
    case "Expired":
      return "Authorisation expired";
    default:
      return status ?? "Unknown";
  }
}

/** Yapily Connect disclosure: must be shown VERBATIM wherever pay-by-bank consent is asked (regulatory; Yapily is the PISP). */
export function DisclosureNotice({
  disclosure,
}: {
  // Structural, so the bank-connections disclosure (same three fields) renders through the same notice.
  disclosure: Pick<YapilyDisclosure, "text" | "termsUrl" | "privacyUrl"> | undefined;
}) {
  if (!disclosure) return null;
  return (
    <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
      <p>{disclosure.text}</p>
      <p className="mt-1.5 space-x-3">
        <a
          href={disclosure.termsUrl ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-foreground"
        >
          Terms &amp; Conditions
        </a>
        <a
          href={disclosure.privacyUrl ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-foreground"
        >
          Privacy Notice
        </a>
      </p>
    </div>
  );
}

interface PayByBankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billId: string;
  amount: number | string | null | undefined;
  currency: string;
  supplierName?: string | null;
}

/** The bank redirects back to /bills/{billId}/pay/return via the backend callback. */
export function PayByBankDialog({
  open,
  onOpenChange,
  billId,
  amount,
  currency,
  supplierName,
}: PayByBankDialogProps) {
  const api = useDolfinClient();
  const isDemo = useIsDemo();
  const [institutions, setInstitutions] = useState<PaymentInstitution[]>([]);
  const [disclosure, setDisclosure] = useState<YapilyDisclosure | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [initiating, setInitiating] = useState(false);
  const [bankQuery, setBankQuery] = useState("");
  const [connections, setConnections] = useState<BankConnection[]>([]);
  // A saved account id, or "manual" for a typed IBAN.
  const [payFrom, setPayFrom] = useState<string>("manual");
  const [payerIban, setPayerIban] = useState("");

  const fetchInstitutions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await api.GET("/v1/bank-payments/institutions");
      if (error || !data) {
        setLoadError(apiErrorMessage(error, "Failed to load available banks"));
        return;
      }
      setInstitutions(data.institutions ?? []);
      setDisclosure(data.disclosure);
      const payable = (data.institutions ?? []).filter((inst) => inst.supportedCurrencies.includes(currency));
      if (payable.length === 1) setSelectedId(payable[0]!.id ?? null);
    } catch {
      setLoadError("Failed to load available banks");
    } finally {
      setLoading(false);
    }
  }, [api, currency]);

  useEffect(() => {
    if (open) {
      setBankQuery("");
      setPayerIban("");
      fetchInstitutions();
      // Saved accounts are a convenience: without the banking module (or on error) the payer types an IBAN.
      api.GET("/v1/bank-connections").then(({ data }) => setConnections(data?.data ?? []), () => setConnections([]));
    }
  }, [open, fetchInstitutions, api]);

  // Client-side over a list already in memory, so no debounce: there is no request to save.
  const query = bankQuery.trim().toLowerCase();
  // Banks that can pay this currency first, so a EUR payer isn't scrolling past greyed-out UK banks.
  const visibleInstitutions = (query ? institutions.filter((inst) => inst.name?.toLowerCase().includes(query)) : institutions)
    .slice()
    .sort((a, b) => Number(b.supportedCurrencies.includes(currency)) - Number(a.supportedCurrencies.includes(currency)));
  // A bank the search hides is no longer the payer's choice; the pick returns if the search is cleared.
  const chosenId = visibleInstitutions.some((inst) => inst.id === selectedId) ? selectedId : null;
  const chosen = institutions.find((inst) => inst.id === chosenId);
  const needsPayer = currency === "EUR" && !!chosen?.payerAccountRequired;
  const savedAccounts = connections
    .filter((c) => c.institutionId === chosenId)
    .flatMap((c) => c.accounts)
    .filter((a) => a.currency === currency && a.iban);
  const payFromId = savedAccounts.some((a) => a.id === payFrom) ? payFrom : "manual";
  const payerReady = !needsPayer || payFromId !== "manual" || payerIban.trim().length > 0;

  function chooseBank(id: string | null) {
    setSelectedId(id);
    const firstSaved = connections
      .filter((c) => c.institutionId === id)
      .flatMap((c) => c.accounts)
      .find((a) => a.currency === currency && a.iban);
    setPayFrom(firstSaved?.id ?? "manual");
  }

  async function handleContinue() {
    if (!chosenId || initiating) return;
    setInitiating(true);
    try {
      const returnUrl = `${window.location.origin}/bills/${billId}/pay/return`;
      const { data, error } = await api.POST("/v1/bills/{id}/pay/bank", {
        params: { path: { id: billId } },
        body: {
          institutionId: chosenId,
          returnUrl,
          ...(needsPayer
            ? payFromId === "manual"
              ? { payerIban: payerIban.trim() }
              : { payerBankAccountId: payFromId }
            : {}),
        },
      });
      if (error || !data?.authorisationUrl) {
        toast.error(apiErrorMessage(error, "Failed to start the bank payment"));
        setInitiating(false);
        return;
      }
      // Navigating away, so no reset: the button stays disabled.
      window.location.assign(data.authorisationUrl);
    } catch {
      toast.error("Failed to start the bank payment");
      setInitiating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !initiating && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            Pay by bank
          </DialogTitle>
          <DialogDescription>
            Pay {amount != null ? formatCurrency(amount, currency) : "this bill"}
            {supplierName ? ` to ${supplierName}` : ""} directly from your bank account. You&apos;ll
            be securely redirected to your bank to approve the payment.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <LoadingState label="Loading banks..." />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchInstitutions} />
        ) : institutions.length === 0 ? (
          <EmptyState icon={Landmark} title="No banks available">
            Pay by bank is not available right now. You can still pay this bill another way and mark it
            as paid.
          </EmptyState>
        ) : (
          <div className="space-y-2">
            <Label className="text-xs" htmlFor={institutions.length > BANK_SEARCH_THRESHOLD ? "bank-search" : undefined}>
              Choose your bank
            </Label>
            {institutions.length > BANK_SEARCH_THRESHOLD ? (
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="bank-search"
                  value={bankQuery}
                  onChange={(e) => setBankQuery(e.target.value)}
                  placeholder="Search banks"
                  className="h-9 pl-8"
                />
              </div>
            ) : null}
            <div className="max-h-64 space-y-1.5 overflow-auto pr-1">
              {visibleInstitutions.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No bank matches that name.</p>
              ) : null}
              {visibleInstitutions.map((inst) => {
                const supported = inst.supportedCurrencies.includes(currency);
                return (
                  <button
                    key={inst.id}
                    type="button"
                    disabled={!supported}
                    aria-pressed={selectedId === inst.id}
                    onClick={() => chooseBank(inst.id ?? null)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition enabled:hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50 ${
                      selectedId === inst.id ? "border-primary bg-primary/5" : ""
                    }`}
                  >
                    {inst.iconUrl ? (
                      <img src={inst.iconUrl} alt="" className="size-6 shrink-0 object-contain" />
                    ) : null}
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{inst.name}</span>
                      {!supported ? (
                        <span className="block text-xs text-muted-foreground">Doesn&apos;t support {currency} payments</span>
                      ) : null}
                    </span>
                    {selectedId === inst.id && (
                      <CheckCircle2 className="ml-auto size-4 text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {needsPayer ? (
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium">Pay from</legend>
            <p className="text-xs text-muted-foreground">{chosen?.name} needs to know which account the payment comes from.</p>
            {savedAccounts.map((account) => (
              <label key={account.id} className="flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="pay-from"
                  className="accent-primary"
                  checked={payFromId === account.id}
                  onChange={() => setPayFrom(account.id)}
                />
                <span className="min-w-0 flex-1 truncate font-medium">{account.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{account.currency} ••{account.iban?.slice(-4)}</span>
              </label>
            ))}
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <input
                type="radio"
                name="pay-from"
                className="accent-primary"
                checked={payFromId === "manual"}
                onChange={() => setPayFrom("manual")}
              />
              <span className="font-medium">{savedAccounts.length > 0 ? "Another account" : "Enter your IBAN"}</span>
            </label>
            {payFromId === "manual" ? (
              <Input
                aria-label="Your IBAN"
                value={payerIban}
                onChange={(e) => setPayerIban(e.target.value)}
                placeholder="e.g. DE89 3704 0044 0532 0130 00"
                className="h-9 font-mono"
                autoComplete="off"
              />
            ) : null}
          </fieldset>
        ) : null}

        {isDemo ? (
          // Nothing reaches Yapily in a demo, so its disclosure would be untrue here.
          <p className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">Demo: no real bank is contacted.</p>
        ) : (
          <DisclosureNotice disclosure={disclosure} />
        )}

        <DialogFooter>
          <Button variant="ghost" disabled={initiating} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!chosenId || !payerReady || initiating || loading}
            onClick={handleContinue}
          >
            {initiating ? <Spinner className="size-4" /> : <ExternalLink className="size-4" />}
            Continue to your bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Fed live by billPaymentStatusChanged: the parent refetches and re-renders this. */
export function BillPaymentStatusCard({ billId, refreshKey }: { billId: string; refreshKey?: unknown }) {
  const api = useDolfinClient();
  const [payment, setPayment] = useState<BillPaymentResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .GET("/v1/bills/{id}/payments", { params: { path: { id: billId } } })
      .then(({ data }) => {
        if (!cancelled) setPayment(data?.[0] ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, billId, refreshKey]);

  if (loading && !payment) return null;
  if (!payment) return null;

  const inFlight =
    payment.status === "Initiating" ||
    payment.status === "AwaitingAuthorisation" ||
    payment.status === "Authorised" ||
    payment.status === "Pending";
  const failed = payment.status === "Failed" || payment.status === "Expired";
  const approvalsRequired = Number(payment.multiAuthorisationsRequired ?? 0);
  const approvalsReceived = Number(payment.multiAuthorisationsReceived ?? 0);
  const awaitingCoApproval = payment.status === "Pending" && approvalsRequired > approvalsReceived;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Bank payment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          {inFlight && <Spinner className="size-4 text-muted-foreground" />}
          {payment.status === "Completed" && <CheckCircle2 className="size-4 text-green-600" />}
          {failed && <AlertTriangle className="size-4 text-destructive" />}
          <Badge variant={failed ? "destructive" : payment.status === "Completed" ? "default" : "secondary"}>
            {billPaymentStatusLabel(payment.status)}
          </Badge>
        </div>

        {payment.status === "AwaitingAuthorisation" && payment.authorisationUrl && (
          <div className="space-y-1.5">
            <Button asChild size="sm" variant="outline">
              <a href={payment.authorisationUrl}>
                <ExternalLink className="size-4" />
                Continue authorisation at your bank
              </a>
            </Button>
            <p className="text-xs text-muted-foreground">
              This link expires after a few minutes. If your bank shows an error, use{" "}
              <span className="font-medium">Restart Pay by Bank</span> to get a fresh link.
            </p>
          </div>
        )}

        {awaitingCoApproval && (
          <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-2.5 text-xs text-muted-foreground">
            <Users className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Waiting for a second approver: {approvalsReceived} of {approvalsRequired} approvals
              received. Another signatory on the
              bank account must approve this payment in their own banking app.
            </span>
          </div>
        )}

        {failed && payment.errorMessage && (
          <p className="text-xs text-destructive">{payment.errorMessage}</p>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t pt-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Amount</dt>
            <dd className="font-medium tabular-nums">
              {formatCurrency(payment.amount ?? 0, payment.currency ?? "GBP")}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">To</dt>
            <dd className="truncate font-medium">{payment.payeeName}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">Reference</dt>
            <dd className="truncate font-mono text-xs">{payment.reference}</dd>
          </div>
          {payment.completedAt ? (
            <div>
              <dt className="text-xs text-muted-foreground">Completed</dt>
              <dd>{formatDateTime(payment.completedAt, "long")}</dd>
            </div>
          ) : null}
          {/* The bank's own status code. Meaningless to most readers but exactly what support asks for,
              so it stays, quietly. */}
          {payment.isoStatus ? (
            <div>
              <dt className="text-xs text-muted-foreground">Bank status code</dt>
              <dd className="font-mono text-xs">{payment.isoStatus}</dd>
            </div>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}
